import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeftIcon, LoaderIcon } from 'lucide-react';
import { useTemplate } from '../hooks/useTemplate';
import { useClients } from '../hooks/useClients';
import { useBulkSendStore } from '../stores/bulkSendStore';
import { BottomNav } from '../components/BottomNav';
import { ClientSelectorList } from '../components/templates/ClientSelectorList';
import { MessagePreviewCard } from '../components/templates/MessagePreviewCard';
import { BulkSendControls } from '../components/templates/BulkSendControls';
import { useBulkSendTemplate } from '../hooks/useBulkSendTemplate';
import { TEMPLATE_CONSTRAINTS } from '../../lib/types/template.types';
import type { MessageState } from '../stores/bulkSendStore';
import type { ClientRecord } from '../hooks/useClients';
import type { Template } from '../../lib/types/template.types';

// Helper function to replace variables in text
function replaceVariables(text: string, variables: Record<string, any>): string {
  return text.replace(TEMPLATE_CONSTRAINTS.VARIABLE_PATTERN, (match, varName) => {
    const value = variables[varName];
    if (value === undefined || value === null) {
      return ''; // Replace with empty string if missing
    }
    return String(value);
  });
}

// Helper function to generate variables from client data
function getClientVariables(client: ClientRecord): Record<string, any> {
  const firstName = client.name?.split(' ')[0] || client.name;
  const currentDate = new Date().toLocaleDateString('en-US', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
  const currentTime = new Date().toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
  });

  return {
    name: client.name,
    first_name: firstName,
    phone: client.phone,
    goal: client.goal || '',
    date: currentDate,
    time: currentTime,
  };
}

// Helper function to render template text with client variables
function renderTemplateText(template: Template, client: ClientRecord): string {
  const variables = getClientVariables(client);

  const textBlocks = template.content.blocks
    .filter((b) => b.type === 'text')
    .map((b) => (b as any).content);

  const combinedText = textBlocks.join('\n\n');
  return replaceVariables(combinedText, variables);
}

export function TemplateBulkSend() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const preselectedJid = searchParams.get('preselected');

  const [generatingMessages, setGeneratingMessages] = useState(false);

  const { data: template, isLoading: templateLoading } = useTemplate(id);
  const { data: clients = [], isLoading: clientsLoading } = useClients();

  const {
    selectedClientIds,
    messages,
    toggleClient,
    setClients,
    setMessages,
    updateMessage,
    removeMessage,
    updateStatus,
    reset,
  } = useBulkSendStore();

  const { sendBulk, sending, progress } = useBulkSendTemplate();

  // Filter clients with WhatsApp
  const whatsappClients = clients.filter((c) => c.whatsapp_jid !== null);

  // Auto-select preselected client if JID provided
  useEffect(() => {
    if (preselectedJid && whatsappClients.length > 0) {
      const preselectedClient = whatsappClients.find((c) => c.whatsapp_jid === preselectedJid);
      if (preselectedClient) {
        setClients([preselectedClient.id]);
      }
    }
  }, [preselectedJid, whatsappClients.length]); // Simplified dependency

  // Generate messages when clients are selected
  useEffect(() => {
    if (!template || selectedClientIds.size === 0) {
      setMessages([]);
      return;
    }

    const generateMessages = async () => {
      setGeneratingMessages(true);
      try {
        const selectedClients = whatsappClients.filter((c) =>
          selectedClientIds.has(c.id)
        );

        const newMessages: MessageState[] = selectedClients.map((client) => {
          const renderedText = renderTemplateText(template, client);

          return {
            clientId: client.id,
            clientName: client.name,
            jid: client.whatsapp_jid!,
            originalText: renderedText,
            editedText: null,
            status: 'pending' as const,
          };
        });

        setMessages(newMessages);
      } catch (error) {
        console.error('Error generating messages:', error);
      } finally {
        setGeneratingMessages(false);
      }
    };

    generateMessages();
  }, [template?.id, selectedClientIds.size, whatsappClients.length]); // Simplified dependencies

  // Reset store on unmount
  useEffect(() => {
    return () => reset();
  }, [reset]);

  // Loading state
  if (templateLoading || clientsLoading) {
    return (
      <div className="min-h-screen bg-[#F5F5F5] pb-20">
        <div className="bg-white shadow-sm sticky top-0 z-20">
          <div className="p-4 flex items-center gap-3">
            <button
              onClick={() => navigate(-1)}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            >
              <ArrowLeftIcon className="w-5 h-5 text-gray-600" />
            </button>
            <h1 className="text-xl font-semibold text-[#212121]">Send Template</h1>
          </div>
        </div>
        <div className="p-4 space-y-4">
          <div className="bg-white rounded-2xl p-5 shadow-sm animate-pulse">
            <div className="h-4 bg-gray-200 rounded w-1/3 mb-4" />
            <div className="h-3 bg-gray-200 rounded w-2/3 mb-2" />
            <div className="h-3 bg-gray-200 rounded w-1/2" />
          </div>
        </div>
        <BottomNav />
      </div>
    );
  }

  // Template not found
  if (!template) {
    return (
      <div className="min-h-screen bg-[#F5F5F5] pb-20">
        <div className="bg-white shadow-sm sticky top-0 z-20">
          <div className="p-4 flex items-center gap-3">
            <button
              onClick={() => navigate('/templates')}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            >
              <ArrowLeftIcon className="w-5 h-5 text-gray-600" />
            </button>
            <h1 className="text-xl font-semibold text-[#212121]">Template Not Found</h1>
          </div>
        </div>
        <div className="p-4">
          <div className="bg-white rounded-2xl shadow-sm p-6 text-center">
            <h3 className="text-lg font-semibold text-[#212121] mb-2">
              Template Not Found
            </h3>
            <p className="text-gray-600 mb-4">
              The template you're looking for doesn't exist or has been deleted.
            </p>
            <button
              onClick={() => navigate('/templates')}
              className="bg-[#5B8E87] text-white px-6 py-3 rounded-xl font-semibold hover:bg-[#4F7A74] transition-colors"
            >
              Back to Templates
            </button>
          </div>
        </div>
        <BottomNav />
      </div>
    );
  }

  const selectedCount = selectedClientIds.size;
  const messagesArray = Array.from(messages.values());

  // Handle bulk send
  const handleSend = async () => {
    if (!template || messagesArray.length === 0) return;

    try {
      const result = await sendBulk(template.id, messagesArray, (clientId, status, error) => {
        updateStatus(clientId, status, error);
      });

      console.log('Bulk send completed:', result);
    } catch (error) {
      console.error('Error during bulk send:', error);
    }
  };

  // Handle retry failed messages
  const handleRetryFailed = async () => {
    if (!template) return;

    const failedMessages = messagesArray.filter((m) => m.status === 'failed');
    if (failedMessages.length === 0) return;

    // Reset failed messages to pending
    failedMessages.forEach((m) => {
      updateStatus(m.clientId, 'pending');
    });

    // Send again
    await handleSend();
  };

  return (
    <div className="min-h-screen bg-[#F5F5F5] pb-20">
      {/* Header */}
      <div className="bg-white shadow-sm sticky top-0 z-20">
        <div className="p-4 flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <ArrowLeftIcon className="w-5 h-5 text-gray-600" />
          </button>
          <div className="flex-1">
            <h1 className="text-xl font-semibold text-[#212121]">Send Template</h1>
            <p className="text-sm text-gray-500">{template.name}</p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-4 space-y-4 pb-32">
        {/* Section 1: Template Preview */}
        <div className="bg-white rounded-2xl shadow-sm p-5">
          <h2 className="text-lg font-semibold text-[#212121] mb-3">Template Preview</h2>
          <div className="bg-gray-50 rounded-xl p-4">
            <p className="text-sm text-gray-700 whitespace-pre-wrap">
              {template.content.blocks
                .filter((b) => b.type === 'text')
                .map((b) => (b as any).content)
                .join('\n\n')}
            </p>
            {template.variables.length > 0 && (
              <div className="mt-3 pt-3 border-t border-gray-200">
                <p className="text-xs text-gray-500 mb-1">Variables:</p>
                <div className="flex flex-wrap gap-1">
                  {template.variables.map((v) => (
                    <span
                      key={v}
                      className="inline-block px-2 py-1 bg-blue-50 text-blue-700 text-xs rounded-lg"
                    >
                      {`{{${v}}}`}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Section 2: Client Selection */}
        <div className="bg-white rounded-2xl shadow-sm p-5">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-semibold text-[#212121]">Select Recipients</h2>
            <span className="text-sm font-medium text-[#5B8E87]">
              {selectedCount} selected
            </span>
          </div>

          {whatsappClients.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-500 mb-2">No clients with WhatsApp connected</p>
              <button
                onClick={() => navigate('/clients')}
                className="text-[#5B8E87] text-sm font-medium"
              >
                View Clients
              </button>
            </div>
          ) : (
            <ClientSelectorList
              clients={whatsappClients}
              selectedIds={selectedClientIds}
              onToggle={toggleClient}
              onSelectAll={() => setClients(whatsappClients.map((c) => c.id))}
              onDeselectAll={() => setClients([])}
            />
          )}
        </div>

        {/* Section 3: Message Preview (shown when clients selected) */}
        {selectedCount > 0 && (
          <div className="bg-white rounded-2xl shadow-sm p-5">
            <h2 className="text-lg font-semibold text-[#212121] mb-3">
              Message Preview ({messagesArray.length})
            </h2>

            {generatingMessages ? (
              <div className="flex items-center justify-center py-8">
                <LoaderIcon className="w-6 h-6 text-[#5B8E87] animate-spin" />
                <span className="ml-2 text-gray-600">Generating messages...</span>
              </div>
            ) : (
              <div className="space-y-3">
                {messagesArray.map((message) => (
                  <MessagePreviewCard
                    key={message.clientId}
                    message={message}
                    onEdit={updateMessage}
                    onRemove={removeMessage}
                  />
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Send Controls - Fixed at bottom */}
      {selectedCount > 0 && !generatingMessages && (
        <div className="fixed bottom-20 left-0 right-0">
          <BulkSendControls
            messages={messagesArray}
            sending={sending}
            progress={progress}
            onSend={handleSend}
            onRetryFailed={handleRetryFailed}
          />
        </div>
      )}

      <BottomNav />
    </div>
  );
}
